import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import type { Chat } from '../types/chat';
import type { RealtimeChannel } from '@supabase/supabase-js';

export function useChatList() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const messageChannelRef = useRef<RealtimeChannel | null>(null);

  const query = useQuery({
    queryKey: ['chats', user?.id],
    queryFn: async () => {
      try {
        // Get IDs of archived chats
        const { data: archivedChatIds, error: archiveError } = await supabase
          .from('archived_chats')
          .select('chat_id')
          .eq('user_id', user?.id);

        if (archiveError) {
          console.error('Error fetching archived chat IDs:', archiveError);
          throw archiveError;
        }

        // Create a simpler query to fetch non-archived chats with less data
        let chatQuery = supabase
          .from('chats')
          .select(`
            id, 
            created_at,
            updated_at,
            vendor_id,
            chat_participants!inner (
              user_id
            )
          `)
          .eq('chat_participants.user_id', user?.id)
          .order('updated_at', { ascending: false });
        
        // Only add the not-in filter if there are archived chats
        if (archivedChatIds && archivedChatIds.length > 0) {
          const archivedIds = archivedChatIds.map(row => row.chat_id);
          if (archivedIds.length > 0) {
          chatQuery = chatQuery.not('id', 'in', `(${archivedIds.join(',')})`);
          }
        }

        console.log('Fetching basic chats for user:', user?.id);
        
        // Add timeout handling with a longer timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Query timeout')), 30000); // Increase to 30 seconds
        });

        try {
          // Execute the query with timeout
          const queryPromise = chatQuery;
          const result = await Promise.race([
            queryPromise,
            timeoutPromise
          ]);
          
          // Handle the result properly
          const { data: chatData, error: chatError } = result as any;

          if (chatError) {
            console.error('Error fetching chats:', chatError);
            throw chatError;
          }

          if (!chatData || chatData.length === 0) {
            console.log('No chats found for user:', user?.id);
            return [];
          }

          console.log('Successfully fetched basic chats:', chatData.length);
          
          // Get all chat IDs
          const chatIds = chatData.map(chat => chat.id);
          
          // Create chat objects with basic info
          const chatsMap = new Map<string, Chat>();
          chatData.forEach((row: any) => {
            chatsMap.set(row.id, {
              id: row.id,
              created_at: row.created_at,
              updated_at: row.updated_at,
              vendor_id: row.vendor_id,
              participants: [],
            } as Chat);
          });
          
          // Fetch vendors in a separate request
          const vendorIds = chatData
            .filter((chat: any) => chat.vendor_id)
            .map((chat: any) => chat.vendor_id);
            
          if (vendorIds.length > 0) {
            console.log('Fetching vendors:', vendorIds.length);
            const { data: vendors, error: vendorError } = await supabase
              .from('vendors')
              .select('id, store_name, user_id, logo_image')
              .in('id', vendorIds);
              
            if (vendorError) {
              console.error('Error fetching vendors:', vendorError);
            } else if (vendors) {
              // Add vendors to their chats
              vendors.forEach((vendor: any) => {
                const chatsWithThisVendor = Array.from(chatsMap.values())
                  .filter(chat => chat.vendor_id === vendor.id);
                  
                chatsWithThisVendor.forEach(chat => {
                  chat.vendor = vendor;
                });
              });
            }
          }
          
          // Fetch last message for each chat
          console.log('Fetching last message for each chat');
          const lastMessagesPromises = chatIds.map(async (chatId: string) => {
            const { data: messages, error: msgError } = await supabase
              .from('messages')
              .select('id, text, created_at, sender_id')
              .eq('chat_id', chatId)
              .order('created_at', { ascending: false })
              .limit(1);
              
            if (msgError) {
              console.error(`Error fetching last message for chat ${chatId}:`, msgError);
              return null;
            }
            
            if (messages && messages.length > 0) {
              const chat = chatsMap.get(chatId);
              if (chat) {
                return {
                  chatId,
                  message: messages[0]
                };
              }
            }
            return null;
          });
          
          const lastMessagesResults = await Promise.all(lastMessagesPromises);
          const lastMessages = lastMessagesResults
            .filter(Boolean)
            .map(result => ({ 
              chatId: result!.chatId, 
              message: result!.message 
            }));
            
          // Get all sender IDs from last messages
          const senderIds = lastMessages
            .map(m => m.message.sender_id)
            .filter(Boolean);
          
          // Fetch all participants for all chats
          const { data: allChatParticipants, error: participantsError } = await supabase
            .from('chat_participants')
            .select('chat_id, user_id')
            .in('chat_id', chatIds);
            
          if (participantsError) {
            console.error('Error fetching all participants:', participantsError);
            throw participantsError;
          }
          
          // Group participants by chat_id
          const participantsByChatId = new Map<string, { user_id: string }[]>();
          allChatParticipants.forEach(participant => {
            if (!participantsByChatId.has(participant.chat_id)) {
              participantsByChatId.set(participant.chat_id, []);
            }
            participantsByChatId.get(participant.chat_id)?.push(participant);
          });

          // Get all unique user IDs from participants and message senders
          const userIds = new Set<string>();
          
          // Add all participant user IDs
          allChatParticipants.forEach(p => userIds.add(p.user_id));
          
          // Add message sender IDs to userIds
          senderIds.forEach(id => userIds.add(id));
          
          console.log('All user IDs to fetch profiles for:', userIds.size);

          // Fetch profiles for all participants and senders
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select(`
              id, 
              full_name, 
              username, 
              avatar_url
            `)
            .in('id', Array.from(userIds));

          if (profilesError) {
            console.error('Error fetching profiles:', profilesError);
            throw profilesError;
          }

          console.log('Fetched profiles:', profiles?.length);
          
          // Fetch vendor information separately for users who have vendors
          const { data: vendorData, error: vendorError } = await supabase
            .from('vendors')
            .select('id, store_name, user_id')
            .in('user_id', Array.from(userIds));
            
          if (vendorError) {
            console.warn('Error fetching vendor data:', vendorError);
          }
          
          console.log('Fetched vendor data:', vendorData?.length);
          
          // Create vendor map by user_id
          const vendorMap = new Map(vendorData?.map(v => [v.user_id, v]) || []);
          
          // Create profiles map with vendor information
          const profilesMap = new Map(profiles?.map(p => [p.id, {
            ...p,
            vendor: vendorMap.get(p.id) || null
          }]) || []);

          // Add last messages to their chats
          lastMessages.forEach(({ chatId, message }) => {
            const chat = chatsMap.get(chatId);
            if (chat) {
              const senderProfile = profilesMap.get(message.sender_id);
              chat.last_message = {
                id: message.id,
                chat_id: chatId,
                text: message.text,
                created_at: message.created_at,
                sender_id: message.sender_id,
                sender: senderProfile ? {
                  id: senderProfile.id,
                  full_name: senderProfile.full_name,
                  username: senderProfile.username,
                  avatar_url: senderProfile.avatar_url
                } : undefined
              };
            }
          });

          // Add participants to chats
          allChatParticipants.forEach(participant => {
            const chat = chatsMap.get(participant.chat_id);
            if (chat) {
                const profile = profilesMap.get(participant.user_id);
                if (profile && !chat.participants.find(p => p.user_id === participant.user_id)) {
                  chat.participants.push({
                  chat_id: participant.chat_id,
                    user_id: participant.user_id,
                    user: {
                      id: profile.id,
                      full_name: profile.full_name,
                      username: profile.username,
                      avatar_url: profile.avatar_url,
                      vendor: profile.vendor
                    }
                  });
              }
            }
          });

          return Array.from(chatsMap.values());
        } catch (error) {
          console.error('Error in chat list query:', error);
          throw error;
        }
      } catch (error) {
        console.error('Error in chat list query:', error);
        throw error;
      }
    },
    enabled: !!user,
    // Set more robust options to handle errors
    retry: (failureCount, error) => {
      // Retry up to 3 times, but don't retry on timeout errors
      if (error instanceof Error && error.message === 'Query timeout') {
        console.log('Not retrying timeout error');
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Add a longer stale time to reduce frequent refetches
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Handler for new messages to update chat list in real-time
  const handleNewMessage = useCallback(async (payload: any) => {
    if (!user) return;
    
    const newMessage = payload.new;
    const messageChatId = newMessage.chat_id;
    
    console.log('📋 [CHAT_LIST] New message received for chat:', messageChatId);

    try {
      // Fetch sender profile for the new message
      const { data: senderProfile } = await supabase
        .from('profiles')
        .select('id, full_name, username, avatar_url')
        .eq('id', newMessage.sender_id)
        .single();

      // Update the chat list cache directly with the new last message
      queryClient.setQueryData(['chats', user.id], (old: Chat[] | undefined) => {
        if (!old || !Array.isArray(old)) return old;
        
        // Find the chat this message belongs to
        const chatIndex = old.findIndex(chat => chat.id === messageChatId);
        
        if (chatIndex === -1) {
          // Chat not in list yet - trigger a full refresh to include new chat
          console.log('📋 [CHAT_LIST] Chat not found in list, invalidating query');
          queryClient.invalidateQueries({ queryKey: ['chats', user.id] });
          return old;
        }

        // Create updated chat with new last message
        const updatedChats = [...old];
        const updatedChat = {
          ...updatedChats[chatIndex],
          last_message: {
            id: newMessage.id,
            chat_id: messageChatId,
            text: newMessage.text,
            created_at: newMessage.created_at,
            sender_id: newMessage.sender_id,
            sender: senderProfile || undefined
          },
          updated_at: newMessage.created_at
        };

        // Remove from current position
        updatedChats.splice(chatIndex, 1);
        // Add to the beginning (most recent)
        updatedChats.unshift(updatedChat);

        console.log('📋 [CHAT_LIST] Updated chat list with new message, moved chat to top');
        return updatedChats;
      });

      // Also update individual chat messages if it's cached
      queryClient.setQueryData(['chat-messages', messageChatId], (old: any[] | undefined) => {
        if (!old || !Array.isArray(old)) return old;
        
        // Check if message already exists
        if (old.some(msg => msg.id === newMessage.id)) {
          return old;
        }

        const messageWithSender = {
          ...newMessage,
          sender: senderProfile || { id: newMessage.sender_id }
        };

        return [...old, messageWithSender].sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });

    } catch (error) {
      console.error('📋 [CHAT_LIST] Error handling new message:', error);
      // Fallback to invalidating the query
      queryClient.invalidateQueries({ queryKey: ['chats', user.id] });
    }
  }, [user, queryClient]);

  // Subscribe to chat updates and new messages (optimized)
  useEffect(() => {
    if (!user) return;

    let debounceTimeout: NodeJS.Timeout | null = null;
    
    const debouncedInvalidate = () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['chats', user.id] });
      }, 200);
    };

    // Clean up existing message channel
    if (messageChannelRef.current) {
      supabase.removeChannel(messageChannelRef.current);
      messageChannelRef.current = null;
    }

    // Subscribe to all new messages for real-time chat list updates
    const messageChannel = supabase
      .channel(`chat-list-messages-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
      }, handleNewMessage)
      .subscribe((status) => {
        console.log('📋 [CHAT_LIST] Message subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('📋 [CHAT_LIST] ✅ Subscribed to all new messages for chat list');
        }
      });
    
    messageChannelRef.current = messageChannel;

    // Subscribe to chat structure changes
    const chatStructureChannel = supabase
      .channel(`chat-structure-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chats',
      }, debouncedInvalidate)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chat_participants',
        filter: `user_id=eq.${user.id}`,
      }, debouncedInvalidate)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'archived_chats',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        debouncedInvalidate();
        queryClient.invalidateQueries({ queryKey: ['archived-chats', user.id] });
      })
      .subscribe((status) => {
        console.log('📋 [CHAT_LIST] Structure subscription status:', status);
      });

    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      
      // Clean up message channel
      if (messageChannelRef.current) {
        supabase.removeChannel(messageChannelRef.current);
        messageChannelRef.current = null;
      }
      
      // Clean up structure channel
      supabase.removeChannel(chatStructureChannel);
    };
  }, [user, queryClient, handleNewMessage]);

  return query;
} 